import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Bell, Check, CheckCheck, Truck, Building, Receipt, AlertTriangle, MessageCircle, Wrench, Shield, X } from 'lucide-react';

const ICON_MAP = {
  truck: Truck,
  building: Building,
  receipt: Receipt,
  'alert-triangle': AlertTriangle,
  'message-circle': MessageCircle,
  wrench: Wrench,
  shield: Shield,
  bell: Bell,
};

const PRIORITY_STYLES = {
  high: { borderColor: 'var(--error)', bg: 'var(--error-muted)' },
  medium: { borderColor: 'var(--warning)', bg: 'var(--warning-muted)' },
  low: { borderColor: 'var(--border)', bg: 'transparent' },
};

function timeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications', { params: { limit: 100 } });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to mark as read', e);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all as read', e);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2.5 rounded-md transition-colors"
        style={{ color: 'var(--text-muted)' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-[20px] flex items-center justify-center px-1.5 text-[11px] font-bold rounded-full animate-pulse"
            style={{ background: 'var(--error)', color: 'white' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[420px] max-h-[80vh] rounded-lg overflow-hidden z-50 animate-fade-in"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)' }}
          data-testid="notification-dropdown">
          
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b"
            style={{ background: 'var(--surface-elevated)', borderColor: 'var(--border)' }}>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'Manrope' }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide transition-colors disabled:opacity-50"
                style={{ color: 'var(--primary)' }}
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 110px)' }}>
            {notifications.length === 0 ? (
              <div className="py-16 text-center">
                <Bell size={36} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const IconComponent = ICON_MAP[n.icon] || Bell;
                const priorityStyle = PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.low;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className="flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors border-l-[3px]"
                    style={{
                      borderColor: 'var(--border)',
                      borderLeftColor: priorityStyle.borderColor,
                      background: !n.is_read ? 'var(--primary-muted)' : 'transparent',
                    }}
                    onMouseEnter={e => { if (n.is_read) e.currentTarget.style.background = 'var(--surface-hover)' }}
                    onMouseLeave={e => { if (n.is_read) e.currentTarget.style.background = 'transparent' }}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="mt-0.5 p-2 rounded-md"
                      style={{ 
                        background: !n.is_read ? 'var(--primary-muted)' : 'var(--bg)',
                        color: !n.is_read ? 'var(--primary)' : 'var(--text-muted)'
                      }}>
                      <IconComponent size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium truncate"
                          style={{ color: !n.is_read ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                          {n.title}
                        </p>
                        {!n.is_read && (
                          <button
                            onClick={(e) => markAsRead(n.id, e)}
                            className="shrink-0 p-1.5 rounded transition-colors"
                            style={{ color: 'var(--text-muted)' }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.color = 'var(--primary)' }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)' }}
                            title="Mark as read"
                          >
                            <Check size={14} />
                          </button>
                        )}
                      </div>
                      <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--text-muted)' }}>{n.message}</p>
                      <p className="text-xs mt-2 font-mono" style={{ color: 'var(--text-faint)' }}>{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-5 py-3 border-t flex items-center justify-between" style={{ background: 'var(--surface-elevated)', borderColor: 'var(--border)' }}>
              <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
                Showing {notifications.length} notifications
              </p>
              <button
                onClick={() => { navigate('/ops/notifications'); setOpen(false); }}
                className="text-xs font-bold underline transition-colors hover:opacity-80"
                style={{ color: 'var(--primary)' }}
                data-testid="view-all-notifications-link">
                View all →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
